import React, { useState } from 'react';
import { Camera, X, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '../../components/ui/BackButton';
import { SHOPPER_HOME } from '../../constants/routes';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadString, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../../config/firebase';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

// `type` is the backend dispute type each option maps to
const issueTypes = [
  { id: 'not_delivered', type: 'not_received', label: 'Order not delivered', description: 'I never received my order' },
  { id: 'wrong_item', type: 'wrong_items', label: 'Wrong item received', description: 'I received a different item' },
  { id: 'damaged', type: 'damaged', label: 'Item damaged', description: 'The item arrived damaged or defective' },
  { id: 'missing_item', type: 'wrong_items', label: 'Missing items', description: 'Some items were not included' },
  { id: 'quality', type: 'other', label: 'Quality issue', description: 'The item quality doesn\'t match description' },
  { id: 'other', type: 'other', label: 'Other issue', description: 'Something else went wrong' },
];

export function DisputePage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setPhotos(prev => [...prev, e.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!orderId || !selectedIssue) return;
    setLoading(true);
    setError(null);

    try {
      // Upload evidence photos to Storage first
      const photoUrls: string[] = [];
      for (let i = 0; i < photos.length; i++) {
        const photoRef = storageRef(storage, `disputes/${orderId}/${Date.now()}_${i}.jpg`);
        await uploadString(photoRef, photos[i], 'data_url');
        photoUrls.push(await getDownloadURL(photoRef));
      }

      const issue = issueTypes.find((t) => t.id === selectedIssue)!;
      const fileDispute = httpsCallable(functions, 'fileDispute');
      await fileDispute({
        orderId,
        type: issue.type,
        description: description.trim() || issue.label,
        photoUrls,
        clientRequestId: `dispute_${orderId}_${Date.now()}`,
      });

      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-success" />
          </div>
          <h1 className="text-2xl font-serif text-heading mb-3">Report Submitted</h1>
          <p className="text-body mb-8">
            We've received your report for Order #{orderId?.slice(-6)}. 
            Our team will review it and get back to you within 24 hours.
          </p>
          <div className="space-y-3">
            <Button fullWidth onClick={() => navigate(`/orders/${orderId}`)}>
              View Order
            </Button>
            <Button variant="outline" fullWidth onClick={() => navigate(SHOPPER_HOME)}>
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BackButton
                fallback={orderId ? `/orders/${orderId}` : '/orders'}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
              />
              <div className="ml-4">
                <h1 className="text-lg font-serif text-heading">Report an Issue</h1>
                <p className="text-sm text-body">Order #{orderId?.slice(-6)}</p>
              </div>
            </div>
            <span className="text-sm text-body">Step {step} of 3</span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="h-1 bg-gray-100">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Step 1: Select Issue Type */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="text-center mb-6">
                <h2 className="text-xl font-serif text-heading">What went wrong?</h2>
                <p className="text-body mt-1">Select the issue that best describes your problem</p>
              </div>
              
              <div className="space-y-3">
                {issueTypes.map((issue) => (
                  <button
                    key={issue.id}
                    onClick={() => setSelectedIssue(issue.id)}
                    className={cn(
                      "w-full text-left p-4 rounded-xl border-2 transition-all",
                      selectedIssue === issue.id
                        ? "border-primary bg-primary/5"
                        : "border-gray-100 hover:border-gray-200 bg-white"
                    )}
                  >
                    <p className="font-medium text-heading">{issue.label}</p>
                    <p className="text-sm text-body mt-1">{issue.description}</p>
                  </button>
                ))}
              </div>

              <div className="pt-4">
                <Button 
                  fullWidth 
                  size="lg"
                  disabled={!selectedIssue}
                  onClick={() => setStep(2)}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Add Details */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-xl font-serif text-heading">Tell us more</h2>
                <p className="text-body mt-1">Help us understand what happened</p>
              </div>

              <Card>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-heading mb-2">
                      Describe the issue
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Please provide as much detail as possible about what went wrong..."
                      rows={4}
                      className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-heading mb-2">
                      Add photos (optional)
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {photos.map((photo, idx) => (
                        <div key={idx} className="relative w-24 h-24 rounded-xl overflow-hidden bg-gray-100">
                          <img src={photo} alt="" className="w-full h-full object-cover" />
                          <button
                            onClick={() => removePhoto(idx)}
                            className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center"
                          >
                            <X className="h-4 w-4 text-white" />
                          </button>
                        </div>
                      ))}
                      {photos.length < 4 && (
                        <label className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-purple-50 transition-colors">
                          <Camera className="h-6 w-6 text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Add Photo</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-body mt-2">Up to 4 photos. Max 10MB each.</p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex space-x-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button fullWidth size="lg" onClick={() => setStep(3)}>
                  Review
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Review & Submit */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-xl font-serif text-heading">Review your report</h2>
                <p className="text-body mt-1">Make sure everything looks correct</p>
              </div>

              <Card>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <p className="text-sm text-body">Issue Type</p>
                    <p className="font-medium text-heading">
                      {issueTypes.find(i => i.id === selectedIssue)?.label}
                    </p>
                  </div>
                  
                  {description && (
                    <div>
                      <p className="text-sm text-body">Description</p>
                      <p className="text-heading">{description}</p>
                    </div>
                  )}

                  {photos.length > 0 && (
                    <div>
                      <p className="text-sm text-body mb-2">Photos ({photos.length})</p>
                      <div className="flex space-x-2">
                        {photos.map((photo, idx) => (
                          <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden">
                            <img src={photo} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-heading text-sm">What happens next?</p>
                  <p className="text-sm text-body mt-1">
                    Our team will review your report within 24 hours. We may reach out 
                    for additional information. Refunds, if approved, will be processed 
                    within 3-5 business days.
                  </p>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <Button variant="outline" onClick={() => setStep(2)} disabled={loading}>
                  Back
                </Button>
                <Button 
                  fullWidth 
                  size="lg" 
                  onClick={handleSubmit}
                  loading={loading}
                >
                  Submit Report
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}


